import { tokenResponseSchema, type OidcClientConfig, type OidcDiscovery, type TokenResponse } from './types';

export interface BuildAuthUrlInput {
  config: OidcClientConfig;
  discovery: OidcDiscovery;
  state: string;
  nonce: string;
  codeChallenge: string;
}

/** Build the authorization endpoint URL for the OAuth2 + PKCE flow. */
export function buildAuthorizationUrl(input: BuildAuthUrlInput): string {
  const scopes = input.config.scopes ?? ['openid', 'profile', 'email'];
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: input.config.clientId,
    redirect_uri: input.config.redirectUri,
    scope: scopes.join(' '),
    state: input.state,
    nonce: input.nonce,
    code_challenge: input.codeChallenge,
    code_challenge_method: 'S256',
  });
  return `${input.discovery.authorization_endpoint}?${params.toString()}`;
}

export interface BuildLogoutUrlInput {
  config: OidcClientConfig;
  discovery: OidcDiscovery;
  idToken?: string;
  state?: string;
}

/** Build the RP-Initiated Logout URL if the issuer supports it, else null. */
export function buildLogoutUrl(input: BuildLogoutUrlInput): string | null {
  const endpoint = input.discovery.end_session_endpoint;
  if (!endpoint) return null;
  const params = new URLSearchParams({ client_id: input.config.clientId });
  if (input.config.postLogoutRedirectUri) {
    params.set('post_logout_redirect_uri', input.config.postLogoutRedirectUri);
  }
  if (input.idToken) params.set('id_token_hint', input.idToken);
  if (input.state) params.set('state', input.state);
  return `${endpoint}?${params.toString()}`;
}

export interface ExchangeCodeInput {
  config: OidcClientConfig;
  discovery: OidcDiscovery;
  code: string;
  codeVerifier: string;
  fetch?: typeof globalThis.fetch;
  signal?: AbortSignal;
}

/** Exchange an authorization code for tokens at the token endpoint. */
export async function exchangeCodeForTokens(input: ExchangeCodeInput): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: input.config.clientId,
    code: input.code,
    redirect_uri: input.config.redirectUri,
    code_verifier: input.codeVerifier,
  });
  return postToken(input.discovery.token_endpoint, body, input.fetch, input.signal);
}

export interface RefreshTokensInput {
  config: OidcClientConfig;
  discovery: OidcDiscovery;
  refreshToken: string;
  fetch?: typeof globalThis.fetch;
  signal?: AbortSignal;
}

/** Refresh tokens at the token endpoint. */
export async function refreshTokens(input: RefreshTokensInput): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: input.config.clientId,
    refresh_token: input.refreshToken,
  });
  return postToken(input.discovery.token_endpoint, body, input.fetch, input.signal);
}

async function postToken(
  endpoint: string,
  body: URLSearchParams,
  fetchOverride?: typeof globalThis.fetch,
  signal?: AbortSignal,
): Promise<TokenResponse> {
  const fetchImpl = fetchOverride ?? globalThis.fetch.bind(globalThis);
  const response = await fetchImpl(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: body.toString(),
    signal,
  });
  if (!response.ok) {
    let errorBody: unknown = undefined;
    try {
      errorBody = await response.json();
    } catch {
      // ignore
    }
    throw new TokenEndpointError(
      response.status,
      `Token endpoint returned ${response.status}`,
      errorBody,
    );
  }
  const raw = (await response.json()) as unknown;
  const parsed = tokenResponseSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Token endpoint response invalid: ${JSON.stringify(parsed.error.issues)}`);
  }
  return parsed.data;
}

export class TokenEndpointError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'TokenEndpointError';
    this.status = status;
    this.body = body;
  }
}
